import React from "react";
import { MdFolder, MdFolderOpen, MdHome } from "react-icons/md";
import Modal from "components/ui/Modal";
import { useAppLanguage } from "hooks/useAppLanguage";
import type { FolderNode } from "./components/FolderTree";

export type MoveTarget = { id: number | null; title: string };

type Props = {
  open: boolean;
  onClose: () => void;
  tree: FolderNode[];
  title: string;
  /** Folder ids that should not be selectable (eg. self + descendants when moving a folder). */
  disabledIds?: Set<number>;
  /** Whether moving to root (id = null) is allowed. Default true. */
  allowRoot?: boolean;
  onMove: (target: MoveTarget) => Promise<void> | void;
};

export default function MoveToDialog({
  open,
  onClose,
  tree,
  title,
  disabledIds,
  allowRoot = true,
  onMove,
}: Props) {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const [selected, setSelected] = React.useState<MoveTarget | null>(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setSelected(null);
      setSaving(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await onMove(selected);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const renderNodes = (nodes: FolderNode[], depth: number): React.ReactNode =>
    nodes.map((n) => {
      const isDisabled = disabledIds?.has(n.id) ?? false;
      const isSelected = selected?.id === n.id;
      return (
        <div key={n.id}>
          <button
            type="button"
            disabled={isDisabled}
            onClick={() => setSelected({ id: n.id, title: n.title })}
            style={{ paddingInlineStart: `${depth * 16 + 8}px` }}
            className={`flex w-full items-center gap-2 rounded-md py-1.5 pr-2 text-sm transition-colors ${
              isDisabled
                ? "cursor-not-allowed text-gray-400"
                : isSelected
                ? "bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-200"
                : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10"
            }`}
          >
            {isSelected ? (
              <MdFolderOpen className="h-4 w-4 text-amber-500" />
            ) : (
              <MdFolder className="h-4 w-4 text-amber-500" />
            )}
            <span className="truncate">{n.title}</span>
          </button>
          {n.children && n.children.length > 0 && renderNodes(n.children, depth + 1)}
        </div>
      );
    });

  return (
    <Modal open={open} onClose={onClose} title={title} size="md">
      <div className="flex flex-col gap-3">
        <p className="text-xs text-gray-500">
          {isArabic ? "اختر المجلد الوجهة" : "Pick a destination folder"}
        </p>
        <div className="max-h-[50vh] overflow-auto rounded-xl border border-gray-200 p-2 dark:border-white/10">
          {allowRoot && (
            <button
              type="button"
              onClick={() => setSelected({ id: null, title: isArabic ? "الجذر" : "Root" })}
              className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium transition-colors ${
                selected?.id === null
                  ? "bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-200"
                  : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10"
              }`}
            >
              <MdHome className="h-4 w-4" />
              {isArabic ? "الجذر" : "Root"}
            </button>
          )}
          {renderNodes(tree, 0)}
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/10"
          >
            {isArabic ? "إلغاء" : "Cancel"}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selected || saving}
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
          >
            {isArabic ? "نقل هنا" : "Move here"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
